<template>
  <div class="pie-chart-wrapper">
    <div class="chart-content">
      <div class="pie-chart-container" ref="pieChart"></div>
      <div class="charts-label">
        <div 
          class="label-item" 
          v-for="(item, index) in labelData" 
          :key="index"
        >
          <div class="tag" :style="{background: item.color}"></div>
          <div class="tag-text">{{item.text}}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import * as echarts from 'echarts';

export default {
  name: 'PieChart',
  props: {
    // 饼图数据（扇区名称+数值，必传）
    pieData: {
      type: Array,
      required: true,
      default: () => [
        { name: this.$t('巡检.已巡检'), value: 0 },
        { name: this.$t('巡检.未巡检'), value: 0 }
      ]
    },
    // 标签数据（颜色+文本，与pieData顺序对应，必传）
    labelData: {
      type: Array,
      required: true,
      default: () => [
        { color: '#1461E1', text: this.$t('巡检.已巡检')+'0' },
        { color: '#09B2F3', text: this.$t('巡检.未巡检')+'0' }
      ]
    },
    // 中间合计文本的单位（如“辆”“个”）
    totalSuffix: {
      type: String,
      default: "辆" // 默认单位为“辆”，适配车辆场景
    },
    // 图表标题（如“车辆总数”）
    title: {
      type: String,
      default: "数量分布"
    },
    // 饼图扇区颜色（与labelData.color对应）
    pieColors: {
      type: Array,
      default: () => ['#1461E1', '#09B2F3']
    },
    // 标题距离顶部的距离
    titleTop: {
      type: String,
      default: '0px'
    }
  },
  computed: {
    // 计算合计数量（处理非数字/空值，确保为有效数字）
    total() {
      return this.pieData.reduce((sum, item) => {
        // 强制转换为数字，无效值按0计算
        const value = Number(item.value || 0);
        return sum + value;
      }, 0);
    }
  },
  watch: {
    // 监听数据变化，实时更新图表
    pieData: {
      deep: true, // 深度监听数组内元素变化
      handler() {
        this.$nextTick(() => this.updateChart()); // 确保DOM更新后再刷新
      }
    },
    // 监听颜色变化，实时更新图表
    pieColors: {
      deep: true,
      handler() {
        this.$nextTick(() => this.updateChart());
      }
    },
    // 监听父组件传递的标题/单位变化
    title() {
      this.updateChart();
    },
    totalSuffix() {
      this.updateChart();
    }
  },
  mounted() {
    // DOM渲染完成后初始化（加延时确保容器尺寸稳定）
    this.$nextTick(() => {
      setTimeout(() => this.initChart(), 150);
    });
    // 监听窗口大小变化，自适应调整
    window.addEventListener('resize', this.handleResize);
  },
  // 关键新增：路由缓存时进入页面触发（解决切换页面后不显示）
  activated() {
    this.$nextTick(() => {
      setTimeout(() => this.initChart(), 100);
    });
  },
  beforeUnmount() {
    // 销毁图表实例，移除事件监听（避免内存泄漏）
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 初始化图表实例（新增尺寸检查+重试逻辑）
    initChart() {
      const chartDom = this.$refs.pieChart;
      // 检查容器是否存在且有有效尺寸
      if (!chartDom || chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
        console.warn('PieChart容器尺寸异常，100ms后重试初始化');
        setTimeout(() => this.initChart(), 100); // 尺寸无效时延迟重试
        return;
      }

      // 销毁已有实例，避免重复初始化
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }

      // 创建ECharts实例（指定渲染模式为canvas，提升兼容性）
      this.chartInstance = echarts.init(chartDom, null, {
        renderer: 'canvas',
        useDirtyRect: false // 禁用脏矩形优化，确保尺寸变化时完全重绘
      });

      // 初始化时更新图表配置
      this.updateChart();
    },
    // 更新图表配置（数据/颜色变化时调用）
    updateChart() {
      if (!this.chartInstance) return;
      
      // 确保有有效的饼图数据
      const validData = this.pieData.filter(item => 
        typeof item.value === 'number' && !isNaN(item.value)
      );
      
      const total = validData.reduce((sum, item) => sum + item.value, 0);
      
      const option = {
        title: {
          text: this.title,
          left: 'center',
          top: this.titleTop,
          textStyle: { 
            fontSize: 14, 
            fontWeight: 'normal', 
            color: '#333' 
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: `${this.title} <br/>{b}: {c} ${this.totalSuffix} ({d}%)`
        },
        series: [
          // 饼图扇区配置
          {
            name: this.title,
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '50%'],
            data: validData,
            color: this.pieColors,
            label: { show: false },
            labelLine: { show: false },
            emphasis: { disabled: true },
            z: 1
          },
          // 中间合计文本配置
          {
            type: 'pie',
            radius: '0%',
            center: ['50%', '50%'],
            label: {
              show: true,
              position: 'center',
              formatter: () => {
                return `{number|${total}}{unit|${this.totalSuffix}}`;
              },
              rich: {
                number: {
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: '#0E193B',
                  lineHeight: 30
                },
                unit: {
                  fontSize: 12,
                  color: '#0E193B',
                  lineHeight: 30
                }
              }
            },
            data: [{ value: 0 }],
            z: 10
          }
        ]
      };
    
      // 应用配置（true表示全量更新，避免增量更新导致的渲染异常）
      this.chartInstance.setOption(option, true);
    },
    // 窗口大小变化时调整图表（优化防抖逻辑）
    handleResize() {
      if (!this.resizeTimer) {
        this.resizeTimer = setTimeout(() => {
          if (this.chartInstance) {
            this.chartInstance.resize(); // 触发ECharts尺寸重计算
          }
          this.resizeTimer = null;
        }, 50); // 防抖延时，避免频繁触发
      }
    },
    // 暴露给父组件的主动刷新方法（父页面可调用）
    refreshChart() {
      this.$nextTick(() => this.initChart());
    }
  },
  data() {
    return {
      chartInstance: null, // 保存ECharts实例
      resizeTimer: null // 防抖定时器
    };
  }
};
</script>

<style scoped>
/* 组件整体容器（确保继承父容器尺寸） */
.pie-chart-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: inline-block; /* 关键：确保容器跟随父元素宽度自适应 */
  vertical-align: top;
}

/* 饼图+标签的纵向布局容器 */
.chart-content {
  display: flex;
  flex-direction: column;
  height: calc(100% - 60px);
}

/* 饼图容器（强化尺寸稳定性） */
.pie-chart-container {
  flex: 1;
  width: 100%;
  min-height: 160px; /* 最小高度保障，避免被压缩 */
  position: relative;
}

/* 标签容器（水平居中排列） */
.charts-label {
  display: flex;
  justify-content: center;
  gap: 20px; /* 标签间距 */
  margin-top: 16px; /* 与饼图的距离 */
  padding-bottom: 8px;
  flex-wrap: wrap; /* 适配小尺寸屏幕，避免标签溢出 */
}

/* 单个标签项（颜色块+文本） */
.label-item {
  display: inline-flex;
  align-items: center;
}

/* 标签颜色块 */
.tag {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
}

/* 标签文本 */
.tag-text {
  font-weight: 400;
  font-size: 14px;
  color: #0E193B;
  text-align: left;
}
</style>